import { useEffect, useRef, useState } from "react";
import { ATTRIBUTE_OPTION_LIMITS, normaliseAttributeOptions, type GeneratedDefinition, type GeneratedNodeAttribute } from "@keydials/api-client";
import {
  fieldsetTitles,
  ItemScopeTree,
  normaliseFieldsetRuns,
  renameGroup,
  setAllowUserValues,
  setGroup,
  setOnlySomeItems,
  setOptions,
  updateField,
  type SegmentNoun,
} from "@keydials/dial-workspace";
import { builderCopy as copy } from "./copy";
import {
  addOptionRefusal,
  duplicateOptions,
  fieldSettings,
  guidanceHasMarkup,
  ITEM_SCOPE_LABELS,
  onlySomeItemsRefusal,
  optionLabel,
  optionsEmptyLine,
  removeOptionLabel,
  renameGroupView,
  suggestedAnswerInput,
  type SettingView,
} from "./fieldSettingsView";

type Edit = (change: (model: GeneratedDefinition) => GeneratedDefinition) => void;

/** The longest heading the server stores (`fieldsetTitle`), and the longest suggested answer or guidance. */
const GROUP_MAX = 120;
const TEXT_MAX = 2000;

interface FieldSettingsProps {
  /** The area's id, which its More options button controls, and the start of every id inside it. Carries the layout, since both are on the page. */
  id: string;
  hidden: boolean;
  field: GeneratedNodeAttribute;
  model: GeneratedDefinition;
  noun: SegmentNoun;
  locked: boolean;
  edit: Edit;
  onDone: () => void;
}

/** Everything one setting needs. */
interface SettingProps {
  id: string;
  setting: SettingView;
  field: GeneratedNodeAttribute;
  model: GeneratedDefinition;
  locked: boolean;
  edit: Edit;
}

/**
 * A field's settings on Details (KEY-560, Phase 3), opened under its row by More options: exactly the settings the field is offered,
 * in order. Hidden rather than unmounted when closed, so an edit that's saved when a box is left, or a rename half typed, isn't lost.
 * Every change goes through the shared field edits, which keep the server's rules.
 */
export function FieldSettings({ id, hidden, field, model, noun, locked, edit, onDone }: FieldSettingsProps) {
  // Judged once, when the row appears: guidance a template brought as formatted text stays as it is, with no box, and a bracket
  // typed in the builder's own box can't hide the box being typed in.
  const [guidanceIsMarkup] = useState(() => guidanceHasMarkup(field.guidance));

  return (
    <div id={id} className="kd-details-settings" hidden={hidden}>
      {fieldSettings(field, noun, guidanceIsMarkup).map((setting) => (
        <Setting key={setting.id} id={`${id}-${setting.id}`} setting={setting} field={field} model={model} locked={locked} edit={edit} />
      ))}
      <div className="kd-panel-actions">
        <button type="button" className="kd-btn" onClick={onDone}>
          {copy.details.done}
        </button>
      </div>
    </div>
  );
}

function Setting(props: SettingProps) {
  const { setting, field, locked, edit } = props;
  const { target, key } = field;
  const toggle = (checked: boolean | undefined, change: (on: boolean) => (m: GeneratedDefinition) => GeneratedDefinition) => (
    <Switch label={setting.label} checked={Boolean(checked)} disabled={locked} onChange={(on) => edit(change(on))} />
  );

  switch (setting.id) {
    case "options":
      return <OptionsEditor {...props} />;
    case "peopleCanAddTheirOwn":
      return toggle(field.allowUserValues, (on) => (m) => setAllowUserValues(m, target, key, on));
    case "group":
      return <GroupSetting {...props} />;
    case "startsFolded":
      return toggle(field.fieldsetStartsCollapsed, (on) => (m) => updateField(m, target, key, { fieldsetStartsCollapsed: on }));
    case "suggestedAnswer":
      return <SuggestedAnswer {...props} />;
    case "mustAnswerBeforeOpening":
      return toggle(field.requiredAtFirstRun, (on) => (m) => updateField(m, target, key, { requiredAtFirstRun: on }));
    case "mustFillBeforeWork":
      return toggle(field.requiredForNode, (on) => (m) => updateField(m, target, key, { requiredForNode: on }));
    case "onlySomeItems":
      return <OnlySomeItems {...props} />;
    case "allowMoreThanOne":
      return toggle(field.allowMultiple, (on) => (m) => updateField(m, target, key, { allowMultiple: on }));
    case "whatToTellPeople":
      return <WhatToTellPeople {...props} />;
  }
}

function Switch({ label, checked, disabled, describedBy, onChange }: { label: string; checked: boolean; disabled: boolean; describedBy?: string; onChange: (on: boolean) => void }) {
  return (
    <label className="kd-state-done kd-details-switch">
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} aria-describedby={describedBy} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/** The options as they are in the dial being edited, which a change reads rather than the render's copy. */
const optionsIn = (model: GeneratedDefinition, target: string, key: string): string[] =>
  model.nodeAttributes?.find((f) => f.target === target && f.key === key)?.options ?? [];

/** A box per option, each removable, and a way to add another. */
function OptionsEditor({ id, setting, field, locked, edit }: SettingProps) {
  const { target, key } = field;
  const options = field.options ?? [];
  const duplicates = duplicateOptions(options);
  const empty = optionsEmptyLine(field);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const list = useRef<HTMLOListElement>(null);

  const change = (next: (current: string[]) => string[]) => edit((m) => setOptions(m, target, key, next(optionsIn(m, target, key))));

  // A box just added takes the cursor once it's on the page.
  useEffect(() => {
    if (focusIndex === null) return;
    const box = list.current?.querySelectorAll<HTMLInputElement>("input")[focusIndex];
    if (!box) return;
    if (box.offsetParent !== null) box.focus();
    setFocusIndex(null);
  }, [focusIndex, options.length]);

  return (
    <fieldset
      className="kd-details-setting"
      disabled={locked}
      onBlur={(e) => {
        // Tidied as the server will save them once the cursor leaves the options, so what's shown is what's saved. Not while it moves
        // between them, so a box just added isn't tidied away before anything is typed in it.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        change((current) => normaliseAttributeOptions(current));
      }}
    >
      <legend className="kd-details-legend">{setting.label}</legend>
      {empty && <p className="kd-builder-muted">{empty}</p>}
      {options.length > 0 && (
        <ol ref={list} className="kd-details-options">
          {options.map((option, index) => (
            <li key={index} className="kd-details-option">
              <div className="kd-details-option-row">
                <input
                  className="kd-checklist-name"
                  aria-label={optionLabel(index)}
                  aria-describedby={duplicates[index] ? `${id}-repeat-${index}` : undefined}
                  value={option}
                  maxLength={ATTRIBUTE_OPTION_LIMITS.maxLength}
                  onChange={(e) => {
                    const value = e.target.value;
                    change((current) => current.map((o, i) => (i === index ? value : o)));
                  }}
                />
                <button
                  type="button"
                  className="kd-icon-btn"
                  aria-label={removeOptionLabel(index)}
                  onClick={() => {
                    setRefusal(null);
                    change((current) => current.filter((_, i) => i !== index));
                  }}
                >
                  {copy.build.remove}
                </button>
              </div>
              {duplicates[index] && (
                <p id={`${id}-repeat-${index}`} className="kd-builder-error">
                  {copy.details.optionDuplicate}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
      {refusal && (
        <p className="kd-builder-error" role="alert">
          {refusal}
        </p>
      )}
      <div className="kd-panel-actions">
        <button
          type="button"
          className="kd-btn"
          onClick={() => {
            const refused = addOptionRefusal(options);
            setRefusal(refused);
            if (refused) return;
            setFocusIndex(options.length);
            change((current) => [...current, ""]);
          }}
        >
          {copy.details.addOption}
        </button>
      </div>
    </fieldset>
  );
}

/** The heading the field is under, with the headings already used at its level to pick from, and a way to rename the whole group. */
function GroupSetting({ id, setting, field, model, locked, edit }: SettingProps) {
  const { target, key } = field;
  const rename = renameGroupView(model, field);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");

  return (
    <div className="kd-details-setting">
      <label className="kd-builder-field">
        <span>{setting.label}</span>
        <input
          value={field.fieldsetTitle ?? ""}
          placeholder={copy.details.groupPlaceholder}
          maxLength={GROUP_MAX}
          list={`${id}-titles`}
          disabled={locked}
          onChange={(e) => {
            const title = e.target.value;
            edit((m) => setGroup(m, target, key, title));
          }}
          onBlur={() => edit((m) => normaliseFieldsetRuns(m, target))}
        />
      </label>
      <datalist id={`${id}-titles`}>
        {fieldsetTitles(model.nodeAttributes ?? [], target).map((title) => (
          <option key={title} value={title} />
        ))}
      </datalist>
      {rename && !renaming && (
        <button
          type="button"
          className="kd-builder-link kd-details-link"
          disabled={locked}
          onClick={() => {
            setNewName(rename.title);
            setRenaming(true);
          }}
        >
          {rename.button}
        </button>
      )}
      {rename && renaming && (
        <form
          className="kd-panel-confirm"
          aria-label={rename.formLabel}
          onSubmit={(e) => {
            e.preventDefault();
            if (locked || !newName.trim()) return;
            const from = rename.title;
            edit((m) => normaliseFieldsetRuns(renameGroup(m, target, from, newName), target));
            setRenaming(false);
          }}
        >
          <label className="kd-builder-field kd-details-rename">
            <span>{rename.formLabel}</span>
            <input value={newName} maxLength={GROUP_MAX} autoFocus disabled={locked} onChange={(e) => setNewName(e.target.value)} />
          </label>
          <button type="submit" className="kd-btn-primary" disabled={locked || !newName.trim()}>
            {copy.build.rename}
          </button>
          <button type="button" className="kd-btn" onClick={() => setRenaming(false)}>
            {copy.details.cancel}
          </button>
        </form>
      )}
    </div>
  );
}

/** The answer a field suggests, written in the box its kind of answer takes. */
function SuggestedAnswer({ id, setting, field, locked, edit }: SettingProps) {
  const input = suggestedAnswerInput(field);
  if (!input) return null;
  const { target, key } = field;
  const hintId = `${id}-hint`;
  const set = (value: string) => edit((m) => updateField(m, target, key, { defaultValue: value === "" ? null : value }));
  const hint = (
    <p id={hintId} className="kd-builder-muted">
      {copy.details.suggestedAnswerHint}
    </p>
  );

  if (input.kind === "needsOptions") {
    return (
      <div className="kd-details-setting">
        <span className="kd-details-legend">{setting.label}</span>
        {hint}
        <p className="kd-builder-muted">{copy.details.suggestedAnswerNeedsOptions}</p>
      </div>
    );
  }

  const common = { "aria-describedby": hintId, disabled: locked };
  let control;
  switch (input.kind) {
    case "text":
      control = <input {...common} value={input.value} maxLength={TEXT_MAX} onChange={(e) => set(e.target.value)} />;
      break;
    case "longText":
      control = <textarea {...common} className="kd-details-textarea" rows={3} value={input.value} maxLength={TEXT_MAX} onChange={(e) => set(e.target.value)} />;
      break;
    case "date":
      control = <input {...common} type="date" value={input.value} onChange={(e) => set(e.target.value)} />;
      break;
    case "pick":
      control = (
        <select {...common} value={input.value} onChange={(e) => set(e.target.value)}>
          <option value="">{copy.details.noSuggestedAnswer}</option>
          {input.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
      break;
    case "openChoice":
      control = (
        <>
          <input {...common} value={input.value} maxLength={TEXT_MAX} list={`${id}-answers`} onChange={(e) => set(e.target.value)} />
          <datalist id={`${id}-answers`}>
            {input.options.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        </>
      );
      break;
  }

  return (
    <div className="kd-details-setting">
      <label className="kd-builder-field">
        <span>{setting.label}</span>
        {control}
      </label>
      {hint}
    </div>
  );
}

/** What the field tells people when it holds something up: plain text, which is how the screen before a dial opens shows it. */
function WhatToTellPeople({ id, setting, field, locked, edit }: SettingProps) {
  const { target, key } = field;
  const hintId = `${id}-hint`;
  return (
    <div className="kd-details-setting">
      <label className="kd-builder-field">
        <span>
          {setting.label} <em className="kd-panel-optional">{copy.build.optional}</em>
        </span>
        <textarea
          className="kd-details-textarea"
          rows={3}
          maxLength={TEXT_MAX}
          value={field.guidance ?? ""}
          aria-describedby={hintId}
          disabled={locked}
          onChange={(e) => {
            const guidance = e.target.value;
            edit((m) => updateField(m, target, key, { guidance: guidance === "" ? null : guidance }));
          }}
        />
      </label>
      <p id={hintId} className="kd-builder-muted">
        {copy.details.whatToTellPeopleHint}
      </p>
    </div>
  );
}

/** Whether an item field is on every item or only some, and which. Turning it on ticks every item, to untick from. */
function OnlySomeItems({ id, setting, field, model, locked, edit }: SettingProps) {
  const on = Boolean(field.appliesToKeys?.length);
  const refusal = onlySomeItemsRefusal(model, field);
  const noteId = `${id}-note`;
  return (
    <div className="kd-details-setting">
      <Switch
        label={setting.label}
        checked={on}
        disabled={locked || refusal !== null}
        describedBy={refusal ? noteId : undefined}
        onChange={(next) => edit((m) => setOnlySomeItems(m, field.key, next))}
      />
      {refusal && (
        <p id={noteId} className="kd-builder-muted">
          {refusal}
        </p>
      )}
      {on && <ItemScopeTree model={model} fieldKey={field.key} scope={field.appliesToKeys} labels={ITEM_SCOPE_LABELS} disabled={locked} onEdit={edit} />}
    </div>
  );
}
